import * as React from 'react'
import { GlobalDrawer, useDrawerProps } from 'src/design-system'
import { Header } from './header'
import { Chart } from './chart'
import { OrgSkillGapsTableRow } from '../table/data'
import { deserialize } from '../../../api/utils/deserialize'

export const COLUMN_INFO_DRAWER_ID = 'column-info-drawer'

export interface ColumnInfoDrawerProps {
  title?: string
  columnKey?: string
  columnTitle?: string
  columnInfo?: string
  userCount?: number
  rowData?: OrgSkillGapsTableRow
  [key: string]: unknown
}

const DrawerContent: React.FC = () => {
  const { columnTitle, userCount, rowData } =
    useDrawerProps<ColumnInfoDrawerProps>(COLUMN_INFO_DRAWER_ID)

  const [detailedData, setDetailedData] = React.useState<{
    donutChartData?: {
      segments: Array<{
        labelSlug: string
        labelName: string
        color: string
        count: number
      }>
      notCheckedIn: number
      total: number
    }
    individualSkillGaps?: Array<{
      name: string
      requiredLevel: string
      assessedLevel: string
      skillGap: string
    }>
    assessedSkillGap?: number
    averageAssessedLevel?: string
    averageAssessedRequiredLevel?: string
  } | null>(null)
  const [loading, setLoading] = React.useState(false)

  // Fetch detailed data when drawer opens
  React.useEffect(() => {
    const fetchData = async () => {
      if (rowData?.id) {
        // Clear previous data immediately when switching skills
        setDetailedData(null)
        setLoading(true)

        try {
          const data = await fetchDetailedSkillData(rowData.id)
          setDetailedData(data)
        } catch (error) {
          setDetailedData(null)
        } finally {
          setLoading(false)
        }
      }
    }

    fetchData()
  }, [rowData?.id])

  const fetchDetailedSkillData = async (skillId: string) => {
    try {
      // Add cache-busting parameter to ensure fresh data
      const res = await fetch(
        `/api/v1/skill_insights/${skillId}?_t=${Date.now()}`
      )

      if (!res.ok) {
        throw new Error(`Failed to fetch detailed skill data: ${res.status}`)
      }

      const json = await res.json()
      const deserialized = deserialize(json)
      const skillInsight = deserialized?.data?.skillInsights?.[skillId]

      if (!skillInsight) {
        return null
      }

      return skillInsight as {
        donutChartData?: {
          segments: Array<{
            labelSlug: string
            labelName: string
            color: string
            count: number
          }>
          notCheckedIn: number
          total: number
        }
        individualSkillGaps?: Array<{
          name: string
          requiredLevel: string
          assessedLevel: string
          skillGap: string
        }>
        assessedSkillGap?: number
        averageAssessedLevel?: string
        averageAssessedRequiredLevel?: string
      }
    } catch (error) {
      return null
    }
  }

  // Use detailed data if available, otherwise use basic row data
  const donut = detailedData?.donutChartData
  const donutTotal = donut?.total ?? 0
  const chartData = {
    segments: donut
      ? [
          ...donut.segments.map((s) => ({
            labelSlug: s.labelSlug,
            labelName: s.labelName,
            color: s.color,
            percentage: `${Math.round((s.count / (donutTotal || 1)) * 100)}%`,
            count: s.count,
          })),
          {
            labelSlug: 'not_checked_in',
            labelName: 'Not checked-in',
            color: '#686464',
            percentage: `${Math.round(
              (donut.notCheckedIn / (donutTotal || 1)) * 100
            )}%`,
            count: donut.notCheckedIn,
          },
        ]
      : [],
    total: userCount || 0,
    skillGapData: {
      skillGapPercentage:
        (detailedData?.assessedSkillGap || rowData?.assessedSkillGap || 0) > 0
          ? `+${(
              (detailedData?.assessedSkillGap ||
                rowData?.assessedSkillGap ||
                0) * 100
            ).toFixed(0)}%`
          : `${(
              (detailedData?.assessedSkillGap ||
                rowData?.assessedSkillGap ||
                0) * 100
            ).toFixed(0)}%`,
      requireLevel: Number(
        detailedData?.averageAssessedRequiredLevel ||
          rowData?.averageAssessedRequiredLevel ||
          0
      ).toFixed(2),
      assessedLevel: Number(
        detailedData?.averageAssessedLevel || rowData?.averageAssessedLevel || 0
      ).toFixed(2),
      assessedSkillGap:
        detailedData?.assessedSkillGap || rowData?.assessedSkillGap || 0,
      individuals: detailedData?.individualSkillGaps || [],
    },
  }

  return (
    <div className="h-full flex flex-col">
      <Header title={columnTitle || ''} />

      <div className="flex-1 p-6 overflow-y-auto">
        <div className="flex flex-col gap-6">
          <Chart
            segments={chartData.segments}
            total={chartData.total}
            loading={loading}
            skillGapData={chartData.skillGapData}
          />
        </div>
      </div>
    </div>
  )
}

export const ColumnInfoDrawer: React.FC = () => {
  return (
    <GlobalDrawer
      id={COLUMN_INFO_DRAWER_ID}
      title=""
      className="sm:w-[425px] md:w-[425px] [&_.drawer-header]:hidden"
      overlay={false}
    >
      <DrawerContent />
    </GlobalDrawer>
  )
}
